export const sexualRelationshipTypePopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Categorizes the type of interpersonal sexual or romantic relationship
      dynamic described.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Capturing multiple relationship types in a single scene ensures that the
      system recognizes the full scope of romantic or sexual context —
      especially important for accurately assessing tone and context.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "He kissed her gently, brushing the hair from her face as they stood
        under the porch light."
        <br />→ Output: Heterosexual
      </div>
      <div style={{ marginTop: 8 }}>
        "She reached for her girlfriend’s hand, their fingers laced as they
        walked down the hallway."
        <br />→ Output: Homosexual
      </div>
      <div style={{ marginTop: 8 }}>
        "At lunch, Jake kissed his boyfriend on the cheek before heading to
        class. Across the courtyard, Sarah was nervously writing a note to give
        to her crush — one of the new boys from math class."
        <br />→ Output: Homosexual, Heterosexual
      </div>
    </div>
  </div>
);

export const characterAgePopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects/Estimates age group of characters involved.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Detecting character age is essential for determining whether suggestive or
      intimate content involves minors, which significantly raises risk level
      and influences how other signals like tone, physical contact, or power
      dynamics are evaluated.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        Minor: "The two teenagers sat on the bleachers after practice, laughing
        and trading stories about math class."
      </div>
      <div style={{ marginTop: 8 }}>
        Adult: "She adjusted her blazer before stepping into the boardroom —
        another long day of meetings ahead."
      </div>
      <div style={{ marginTop: 8 }}>
        Unknown: "They stood in the hallway, saying nothing, the tension thick
        between them."
      </div>
    </div>
  </div>
);

export const euphemismsListPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detected euphemisms.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Capturing euphemisms allows the pipeline to flag implied sexual or
      romantic content that avoids explicit terms but still conveys
      inappropriate meaning — helping detect subtle risk in otherwise
      clean-looking text. This field is especially critical in literary fiction,
      YA romance, or grooming narratives, where suggestiveness often hides in
      figurative or poetic language. Flags metaphorical or poetic phrasing.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "They explored each other under the safety of moonlight, unspoken
        feelings finally surfacing."
        <br />→ Output: ["explored each other"]
      </div>
      <div style={{ marginTop: 8 }}>
        "What happened between them stayed behind the closed door, never spoken
        of again."
        <br />→ Output: ["what happened between them", "behind the closed door"]
      </div>
      <div style={{ marginTop: 8 }}>
        "She gave herself to him completely, wrapped in the warmth of their
        shared silence."
        <br />→ Output: ["gave herself to him completely"]
      </div>
    </div>
  </div>
);

export const suggestivePhrasesListPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of suggestive phrases found.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      This field identifies literal but emotionally charged or intimate actions,
      helping the pipeline flag content that may be suggestive in behavior, even
      when no explicit or metaphorical language is used — making it critical for
      detecting low-level romantic or sensual tone in realistic fiction. Often
      involves body language, proximity, physical setup. These are literal,
      surface-level actions that could easily be overlooked if only scanning for
      metaphors.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "She lay down beside him, their fingers barely touching."
        <br />→ Output: ["She lay down beside him", "fingers barely touching"]
      </div>
      <div style={{ marginTop: 8 }}>
        "He leaned in close, brushing her hair behind her ear."
        <br />→ Output: ["leaned in close", "brushing her hair"]
      </div>
      <div style={{ marginTop: 8 }}>
        "They stood face to face, breathing in sync, neither saying a word."
        <br />→ Output: ["stood face to face", "breathing in sync"]
      </div>
    </div>
  </div>
);

export const powerImbalancePresentPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects adult-minor or authority dynamic.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Detecting power_imbalance_present is essential for identifying content
      where consent, coercion, or grooming risk may be elevated, even if the
      scene appears subtle or non-explicit — especially when combined with other
      signals like tone, physical_contact_implied, or suggestive_phrases_found.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        True: "She waited after class while the teacher closed the door behind
        them, his tone shifting to something more personal."
      </div>
      <div style={{ marginTop: 8 }}>
        False: "The two teens walked home together, talking about their shared
        science project."
      </div>
    </div>
  </div>
);

export const tonePopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Evaluates emotional tone.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The tone field helps the pipeline evaluate the emotional atmosphere of a
      scene, allowing it to distinguish between safe, academic, or nurturing
      interactions and those that carry romantic, suggestive, or coercive
      undertones — especially when paired with fields like character_age,
      physical_contact_implied, or power_imbalance_present.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        Playful/Flirty: "If you keep looking at me like that," she said with a
        grin, "I’m going to fail this exam."
      </div>
      <div style={{ marginTop: 8 }}>
        Tender: "She wrapped the blanket around his shoulders and sat beside him
        without saying a word."
      </div>
      <div style={{ marginTop: 8 }}>
        Authoritative: "'You are not allowed to speak to him again. Do you
        understand me?' the principal said sternly.'"
      </div>
      <div style={{ marginTop: 8 }}>
        Suggestive: "He leaned in, his voice barely a whisper, and brushed a
        strand of hair from her cheek."
      </div>
      <div style={{ marginTop: 8 }}>
        Neutral: "She opened her notebook, copied the formula from the board,
        and waited for the bell to ring."
      </div>
    </div>
  </div>
);

export const excessiveFocusOnBodyDescriptionPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects whether the text lingers on or over-describes a character’s
      physical body, especially in a way that draws unnecessary, sensual, or
      disproportionate attention to appearance or anatomy.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The excessive_focus_on_body_description field helps the pipeline identify
      objectifying or suggestive narration, especially when subtle or
      non-explicit, flagging content that may appear inappropriate when paired
      with other signals like character_age, tone, or nudity_type.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "Her dress clung to her curves, outlining the dip of her waist and the
        rise of her chest. Every step revealed the smoothness of her legs, the
        shimmer of her skin catching the light."
        <br />→ Output: true
      </div>
      <div style={{ marginTop: 8 }}>
        "She was tall and athletic, her long hair tied back as she stepped onto
        the court."
        <br />→ Output: false
      </div>
    </div>
  </div>
);

export const languageOfSecrecyOrConcealmentPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Flags whether characters use language that suggests hiding, secrecy, or
      concealment, such as "don’t tell anyone," "just between us," or "no one
      can find out."
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The language_of_secrecy_or_concealment field helps identify situations
      where characters are encouraged to hide interactions, a common red flag in
      grooming or exploitative dynamics — especially when combined with
      power_imbalance_present, tone, or character_age.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "No one can know about this, okay? Promise me you won’t say anything to
        your parents."
        <br />→ Output: true
      </div>
      <div style={{ marginTop: 8 }}>
        "She told her mom everything that happened that day — even the awkward
        parts."
        <br />→ Output: false
      </div>
    </div>
  </div>
);

export const roleplayOrFantasyContextPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Identifies whether the scene is framed as a dream, fantasy, daydream,
      internal imagination, or roleplay scenario, rather than occurring in
      direct narrative reality.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The roleplay_or_fantasy_context field helps the pipeline distinguish
      between real interactions and imagined ones, which is crucial for
      assessing intent, severity, and risk.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "In her head, she imagined what it would feel like to kiss him — the way
        he might hold her hand, say her name softly. But then the bell rang, and
        the fantasy broke."
        <br />→ Output: true
      </div>
      <div style={{ marginTop: 8 }}>
        "He kissed her on the forehead before heading to class, their fingers
        still loosely intertwined."
        <br />→ Output: false
      </div>
    </div>
  </div>
);

export const voilenceOverlapPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Flags whether aggression, coercion, force, or violence is present within
      or alongside sexual or suggestive content.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The violence_overlap field helps identify scenes where sexual or romantic
      content is paired with aggression or coercion, signaling higher risk for
      abuse, trauma, or grooming — making it essential for safety screening when
      used with fields like physical_contact_implied, power_imbalance_present,
      or character_age.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "He grabbed her wrist as she tried to back away, whispering that she
        knew she wanted this."
        <br />→ Output: true
      </div>
      <div style={{ marginTop: 8 }}>
        "They sat close together, laughing as their hands brushed, both clearly
        nervous but happy."
        <br />→ Output: false
      </div>
    </div>
  </div>
);

export const nudityDetectedPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Detects if nudity to any extent is detected, and determines if it is
      non-sexual or sexual. If unsure, but clear nudity exists then chooses
      "general".
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The nudity_detected field captures whether nudity exists at all, and how
      it’s framed, helping the system distinguish between benign and risky
      nudity even when visual details (handled by nudity_type) are minimal or
      unclear — supporting better severity assessment and content filtering.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "She stepped out of the shower and reached for the towel, shivering as
        the cold air hit her skin."
        <br />→ Output: general
      </div>
      <div style={{ marginTop: 8 }}>
        "The patient lay naked on the examination table as the doctor conducted
        the routine checkup."
        <br />→ Output: non-sexual
      </div>
      <div style={{ marginTop: 8 }}>
        "She let the dress fall to the floor, his gaze tracing every inch of her
        bare skin as she moved toward him."
        <br />→ Output: sexual
      </div>
    </div>
  </div>
);

export const nudityExtentPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Classifies the extent to which nudity is detected in the text, from no
      implication to explicit references.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The nudity_implied field helps the pipeline recognize non-visual,
      suggestive cues that imply nudity without directly stating it, which is
      essential for detecting borderline or literary nudity often found in
      classic, poetic, or emotionally nuanced texts.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "She stepped behind the curtain and let the robe slip from her
        shoulders, the fabric whispering to the floor."
        <br />→ Output: subtly implied
      </div>
      <div style={{ marginTop: 8 }}>
        "He stood naked in front of the mirror, studying the marks on his
        chest."
        <br />→ Output: explicit
      </div>
    </div>
  </div>
);

export const nudityTypePopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Categorizes the degree and style of nudity or body exposure described in
      the text — whether partial, implied through clothing, or explicitly
      stated.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      The nudity_type field allows the system to distinguish between clinical,
      accidental, or stylized exposure and graphic or intentional nudity,
      providing critical nuance for evaluating severity, intent, and
      appropriateness — especially when paired with fields like tone,
      character_age, or physical_contact_implied.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "She pulled the towel tighter around her chest as he turned away, giving
        her a moment of privacy."
        <br />→ Output: partial, top
      </div>
      <div style={{ marginTop: 8 }}>
        "His shirt clung to his body, soaked from the rain, outlining every
        contour."
        <br />→ Output: clingy, revealing
      </div>
    </div>
  </div>
);

export const sexualActsDetectedPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Identifies and categorizes specific sexual acts described or implied in
      the content, whether explicit or euphemistic.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Allows the pipeline to distinguish between different levels of sexual
      activity, which is critical for assigning severity.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "He pulled her close and kissed her deeply, their breath quickening as
        the world seemed to blur around them."
        <br />→ Output: passionate kissing
      </div>
      <div style={{ marginTop: 8 }}>
        "He kissed her again, slower this time, fingers fumbling with the
        buttons of her shirt. She didn’t stop him."
        <br />→ Output: kissing, undressing, intimate touching
      </div>
    </div>
  </div>
);
